import React, { useState, useRef, useEffect } from "react";
import { FolderOpen, Save, FilePlus, Trash2, Pencil, Check, X, ChevronDown } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { useComposition } from "../contexts/CompositionContext";
import { toast } from "sonner";

/**
 * ProjectsToolbar — affiché en haut de chaque configurateur.
 * Permet à l'utilisateur de :
 *  - Sauvegarder le montage courant sous un nom ("Pharmacie de la Gare")
 *  - Mettre à jour le projet actif
 *  - Charger / supprimer un projet existant
 *  - Démarrer un nouveau projet (vide)
 *
 * Stockage 100% localStorage — aucun compte requis.
 */
export default function ProjectsToolbar() {
  const { lang } = useI18n();
  const {
    projects, activeProject, activeProjectId,
    saveAsNewProject, saveActiveProject, loadProject, deleteProject, renameProject, newProject,
  } = useComposition();

  const [menuOpen, setMenuOpen] = useState(false);
  const [namingMode, setNamingMode] = useState(null); // "save-as" | "rename" | null
  const [draftName, setDraftName] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const menuRef = useRef(null);

  // Fermer le menu au clic extérieur
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const startSaveAs = () => {
    setNamingMode("save-as");
    setDraftName(activeProject?.name ? `${activeProject.name} (copie)` : "");
    setMenuOpen(false);
  };

  const confirmSaveAs = () => {
    const id = saveAsNewProject(draftName);
    if (id) {
      toast.success(lang === "fr" ? `Projet "${draftName.trim()}" enregistré` : `Project "${draftName.trim()}" saved`);
      setNamingMode(null);
      setDraftName("");
    } else {
      toast.error(lang === "fr" ? "Nom de projet requis" : "Project name required");
    }
  };

  const cancelNaming = () => { setNamingMode(null); setDraftName(""); };

  const handleSave = () => {
    if (activeProjectId) {
      saveActiveProject();
      toast.success(lang === "fr" ? `Projet "${activeProject?.name}" mis à jour` : `Project "${activeProject?.name}" updated`);
    } else {
      startSaveAs();
    }
  };

  const handleLoad = (id) => {
    const name = projects.find((p) => p.id === id)?.name;
    setMenuOpen(false);
    toast.info(lang === "fr" ? `Chargement de "${name}"…` : `Loading "${name}"…`);
    loadProject(id);
  };

  const handleDelete = (id, name) => {
    deleteProject(id);
    toast.success(lang === "fr" ? `Projet "${name}" supprimé` : `Project "${name}" deleted`);
  };

  const handleNewProject = () => {
    setMenuOpen(false);
    toast.info(lang === "fr" ? "Nouveau projet vierge…" : "New blank project…");
    newProject();
  };

  const startRename = (id, currentName) => {
    setRenamingId(id);
    setDraftName(currentName);
  };

  const confirmRename = (id) => {
    if (draftName.trim()) {
      renameProject(id, draftName);
      toast.success(lang === "fr" ? "Projet renommé" : "Project renamed");
    }
    setRenamingId(null);
    setDraftName("");
  };

  return (
    <div data-testid="projects-toolbar"
      className="flex items-center gap-2 flex-wrap border border-white/10 bg-[#070707] px-3 py-2 mb-3">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 shrink-0">
        {lang === "fr" ? "Projet" : "Project"} :
      </span>

      {/* Statut projet actif */}
      <span data-testid="active-project-label"
        className={`font-mono text-[11px] tracking-wide ${activeProject ? "text-[#39FF14]" : "text-zinc-500 italic"}`}>
        {activeProject ? activeProject.name : (lang === "fr" ? "(non enregistré)" : "(unsaved)")}
      </span>

      <div className="ml-auto flex items-center gap-2 flex-wrap">
        {/* Enregistrer */}
        <button type="button" onClick={handleSave} data-testid="save-project-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10 hover:bg-[#39FF14]/20 font-mono text-[10px] uppercase tracking-widest">
          <Save size={12} />{lang === "fr" ? "Enregistrer" : "Save"}
        </button>

        {/* Enregistrer sous (toujours nouveau) */}
        {activeProjectId && (
          <button type="button" onClick={startSaveAs} data-testid="save-as-project-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/15 text-zinc-300 hover:border-white/40 font-mono text-[10px] uppercase tracking-widest">
            <Save size={12} />{lang === "fr" ? "Enreg. sous…" : "Save as…"}
          </button>
        )}

        {/* Menu mes projets */}
        <div className="relative" ref={menuRef}>
          <button type="button" onClick={() => setMenuOpen((v) => !v)} data-testid="open-projects-menu"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/15 text-zinc-300 hover:border-white/40 font-mono text-[10px] uppercase tracking-widest">
            <FolderOpen size={12} />
            {lang === "fr" ? "Mes projets" : "My projects"} ({projects.length})
            <ChevronDown size={11} className={`transition-transform ${menuOpen ? "rotate-180" : ""}`} />
          </button>
          {menuOpen && (
            <div data-testid="projects-menu" className="absolute right-0 mt-1 w-[320px] bg-[#0a0a0a] border border-white/15 shadow-[0_24px_48px_-12px_rgba(0,0,0,0.7)] z-50">
              <div className="max-h-[300px] overflow-y-auto">
                {projects.length === 0 && (
                  <div className="px-4 py-6 text-center font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    {lang === "fr" ? "Aucun projet enregistré" : "No saved project yet"}
                  </div>
                )}
                {projects.map((p) => {
                  const isRenaming = renamingId === p.id;
                  const isActive = p.id === activeProjectId;
                  return (
                    <div key={p.id} data-testid={`project-row-${p.id}`}
                      className={`flex items-center gap-2 px-3 py-2 border-b border-white/5 ${isActive ? "bg-[#39FF14]/5" : "hover:bg-white/5"}`}>
                      {isRenaming ? (
                        <>
                          <input type="text" autoFocus value={draftName}
                            onChange={(e) => setDraftName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") confirmRename(p.id); if (e.key === "Escape") { setRenamingId(null); setDraftName(""); } }}
                            className="flex-1 bg-black border border-[#39FF14]/40 px-2 py-1 font-mono text-[11px] text-white focus:outline-none focus:border-[#39FF14]" />
                          <button type="button" onClick={() => confirmRename(p.id)} className="p-1 text-[#39FF14] hover:bg-[#39FF14]/10">
                            <Check size={12} />
                          </button>
                          <button type="button" onClick={() => { setRenamingId(null); setDraftName(""); }} className="p-1 text-zinc-400 hover:bg-white/10">
                            <X size={12} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button type="button" onClick={() => handleLoad(p.id)} data-testid={`load-project-${p.id}`}
                            className="flex-1 text-left">
                            <div className={`font-mono text-[11px] tracking-wide truncate ${isActive ? "text-[#39FF14]" : "text-zinc-200"}`}>
                              {p.name}{isActive && " ●"}
                            </div>
                            <div className="font-mono text-[9px] text-zinc-500 truncate">
                              {new Date(p.updatedAt).toLocaleString(lang === "fr" ? "fr-CH" : "en-US")}
                            </div>
                          </button>
                          <button type="button" onClick={() => startRename(p.id, p.name)} data-testid={`rename-project-${p.id}`}
                            className="p-1 text-zinc-400 hover:text-[#39FF14] hover:bg-[#39FF14]/10" title={lang === "fr" ? "Renommer" : "Rename"}>
                            <Pencil size={11} />
                          </button>
                          <button type="button" onClick={() => handleDelete(p.id, p.name)} data-testid={`delete-project-${p.id}`}
                            className="p-1 text-zinc-400 hover:text-red-400 hover:bg-red-500/10" title={lang === "fr" ? "Supprimer" : "Delete"}>
                            <Trash2 size={11} />
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="border-t border-white/10 p-2">
                <button type="button" onClick={handleNewProject} data-testid="new-project-btn"
                  className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 border border-dashed border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14]/10 font-mono text-[10px] uppercase tracking-widest">
                  <FilePlus size={12} />{lang === "fr" ? "Nouveau projet vierge" : "New blank project"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modale inline "Enregistrer sous…" */}
      {namingMode === "save-as" && (
        <div data-testid="save-as-modal" className="w-full flex items-center gap-2 mt-2 pt-2 border-t border-white/10">
          <input type="text" autoFocus value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") confirmSaveAs(); if (e.key === "Escape") cancelNaming(); }}
            placeholder={lang === "fr" ? "Nom du projet (ex: Pharmacie de la Gare)" : "Project name"}
            data-testid="save-as-input"
            className="flex-1 bg-black border border-[#39FF14]/40 px-3 py-2 font-mono text-[11px] text-white focus:outline-none focus:border-[#39FF14]" />
          <button type="button" onClick={confirmSaveAs} data-testid="confirm-save-as"
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10 hover:bg-[#39FF14]/20 font-mono text-[10px] uppercase tracking-widest">
            <Check size={12} />{lang === "fr" ? "Enregistrer" : "Save"}
          </button>
          <button type="button" onClick={cancelNaming}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-white/15 text-zinc-400 hover:border-white/40 font-mono text-[10px] uppercase tracking-widest">
            <X size={12} />{lang === "fr" ? "Annuler" : "Cancel"}
          </button>
        </div>
      )}
    </div>
  );
}
